import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: unknown;
  /** Re-run the loader; resolves once state has been updated. */
  reload: () => Promise<void>;
  /** Replace the loaded value locally (after a mutation that returns fresh data). */
  set: (value: T) => void;
}

/**
 * Loads data on mount and whenever `deps` change, aborting the in-flight request so a slow
 * response cannot overwrite a newer one.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const controllerRef = useRef<AbortController | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  const run = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const value = await loaderRef.current(controller.signal);
      if (controller.signal.aborted || !aliveRef.current) return;
      setData(value);
      setError(null);
    } catch (cause) {
      if (controller.signal.aborted || !aliveRef.current) return;
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(cause);
    } finally {
      if (!controller.signal.aborted && aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, reload: run, set: setData };
}
